import React, { useMemo } from 'react';
import _ from 'lodash';
import { Typography, Checkbox, Divider } from "antd";
import { getFilterProps } from "utils/tableUtils";
import { TableContainer } from "components/TableContainer";
import { Sorters, CellRenderers } from "utils/tableUtils";
import { EyeOutlined } from "@ant-design/icons";
import DeleteButton from 'components/DeleteButton';
import { SeverityIcons, DownloadIcon } from "components/Icons";
import { API_TOKEN_TYPE_LIST } from "./armorcodeApi/ArmorCodeApiForm";
import Vocabulary from 'components/Vocabulary';
import AuthorisedSwitch from 'components/AuthorisedSwitch';
import { IntegrationType, generateOperationalStatusColumn, generateScanStatusColumn } from './common';
import { PermissionSetMap } from 'constants/RBACConstants';
import AuthorisedContainer from 'components/AuthorisedContainer';
import ActionMenuV2 from 'components/ActionMenuV2';
import { downloadPreConfiguredScript } from './IngestionScriptFormPopup';
import message from "components/CustomMessage";
import { deleteCIConfig, showCIConfig, getCIActionOptions } from 'views/Integrations/CIConfiguration/CIConfiguration';
import EnvironmentName from 'views/GlobalSettings/EnvironmentName';
import { useLocation, Link } from 'react-router-dom';
import { ToggleButton } from './IntegrationsTable';


export default function WebhookIntegrationTable({ data, onUpdateConfigComponent, showHideForm ,webHookEdit, ...integrationData}){
  const { state } = useLocation();
  const {
    defaultProductFilter,
    defaultSubproductFilter,
    defaultEnvironmentFilter,
    defaultOperationalStatusFilter,
    defaultTab,
  } = state || {};

  const defaultFilter = useMemo(() => {
    if (
      // defaultTab is same for the entire page
      // take the default filters only if your tab is set in location state
      (defaultTab === IntegrationType.WEBHOOK && webHookEdit) ||
      (defaultTab === IntegrationType.PUSH && !webHookEdit)
    ) {
      return {
        product_name: [defaultProductFilter],
        sub_product_name: [defaultSubproductFilter],
        environment: [defaultEnvironmentFilter],
        operationalStatus: defaultOperationalStatusFilter ? [defaultOperationalStatusFilter] : undefined,
      };
    }
    return {};
  }, [
    defaultProductFilter,
    defaultSubproductFilter,
    defaultEnvironmentFilter,
    defaultOperationalStatusFilter,
    defaultTab,
    webHookEdit,
  ]);

  const { name, extras }= integrationData;
  const pushBasedColumns = [
    {
      title: <Vocabulary name="product" />,
      dataIndex: "product_name",
      sorter: Sorters.STRING("product_name"),
      render: (text, record) => record.product_id ? (
        <Link to={`/product/${record.product_id}`} title={text.product_name}>
          {text}
        </Link>
      ) : text,
      ...getFilterProps({dataIndex: "product_name", dataSource: data})
    },
    {
      title: <Vocabulary name="subProduct" />,
      dataIndex: "sub_product_name",
      sorter: Sorters.STRING("sub_product_name"),
      render: (text, record) => record.sub_product_id ? (
        <Link to={`/subProduct/${record.sub_product_id}`} title={text.sub_product_name}>
          {text}
        </Link>
      ) : text,
      ...getFilterProps({dataIndex: "sub_product_name", dataSource: data})
    },
    {
      title:"Environment",
      dataIndex: "environment",
      width: 120,
      sorter: Sorters.STRING("environment"),
      render: (value, record) => <EnvironmentName name={record.environment} />,
      ...getFilterProps({dataIndex: "environment", filterLabelFormatter: value => CellRenderers.VOCABULARY_RENDERER(value), dataSource: data})
    },
    generateScanStatusColumn(data),
    {
      title:"Last Run",
      dataIndex: "lastConnected",
      render: CellRenderers.TIME_RELATIVE
    },
    generateOperationalStatusColumn(data),
    {
      title: "Config",
      dataIndex: "suspended",
      sorter: Sorters.STRING("suspended"),
      render:(text,record)=>{
        return   <ToggleButton text={text} record={record} integrationData={integrationData} onUpdateConfigComponent={onUpdateConfigComponent}/>
      }
    },
    {
      title: "Action",
      dataIndex: "id",
      key: 'action',
      render: (id, record) => {
        const onDelete = () => {
          if(integrationData?.deleteAPI){
            const { promise } = integrationData.deleteAPI(record.tool_name, id)
            promise.then(() => {
              onUpdateConfigComponent()
              message.success('Configuration deleted successfully.');
            })
            return promise
          }
        }

        const items = webHookEdit
        ?
        [{ type: "delete",
          permission: PermissionSetMap.DELETE_TOOL,
          promptMessage: <b>Deleting configs will delete Scans and Related Findings.</b>
        }]
        :
        [...[{
          type: "edit",
          permission: PermissionSetMap.EDIT_TOOL_CONFIG
        }, {
          type: "download_python_script",
          text: "Download Python Script",
          icon: <DownloadIcon />,
        }], {
            type: "download_bash_script",
            text: "Download Bash Script",
            icon: <DownloadIcon />,
          },
        ...[{
          type: "delete",
          permission: PermissionSetMap.DELETE_TOOL,
          promptMessage: <b>Deleting configs will delete Scans and Related Findings.</b>
        }, {
          type: "scan result",
          text: "Scan Result",
          icon: <EyeOutlined />,
          tooltip: "Coming Soon!",
          disabled: true
        }],
        ...getCIActionOptions(record)
      ]

        const onClick = async (name, record) => {
          switch(record.name){
            case "edit":
              showHideForm(record.data)
              break;
            case "delete":
              await onDelete()
              break;
            case "download_python_script":
              downloadPreConfiguredScript(record.data);
              break;
            case "download_bash_script":
              downloadPreConfiguredScript({...record.data, downloadFileType: 'bash'});
              break;
            case "scan result":
              // add function here for scan result action
              break;
            case "configure":
            case "edit_configuration":
              showCIConfig(record, onUpdateConfigComponent);
              break;
            case "delete_configuration":
              deleteCIConfig(record, onUpdateConfigComponent);
              break;
            default:
              break;
          }
        }

        return (
            <ActionMenuV2 items={items} onClick={onClick} data={record} />
        )
      },
    }
  ]

  const getFieldRendering = (value) => {
    return <Checkbox defaultChecked={value} disabled></Checkbox>
}

  const emailColumns = [{
    title: 'Send Email for the following entities',
    dataIndex: 'severityConfigType',
    render: text => <><AuthorisedSwitch permission={PermissionSetMap.EDIT_TOOL_CONFIG} defaultChecked={text} disabled checkedChildren="ON" unCheckedChildren="OFF"/> {  text  } </>,
  }, {
  title: (<><SeverityIcons.Critical />  Critical</>),
    dataIndex: 'isCritical',
    render:(value,obj) => getFieldRendering(value,obj,'isCritical') 
  }, {
    title: (<><SeverityIcons.High />  High</>),
    dataIndex: 'isHigh',
    render: (value,obj) => getFieldRendering(value,obj,'isHigh')
  }, {
    title: (<><SeverityIcons.Medium />  Medium</>),
    dataIndex: 'isMedium',
    render:(value,obj) =>  getFieldRendering(value,obj,'isMedium')
  }, {
    title: (<><SeverityIcons.Low />  Low</>),
    dataIndex: 'isLow',
    render: (value,obj) => getFieldRendering(value,obj,'isLow')
  }, {
    title: (<><SeverityIcons.Info />  Info</>),
    dataIndex: 'isInformational',
    render: (value,obj) => getFieldRendering(value,obj,'isInformational')
  }];

  const armorCodeApi = [{
    title: "API Key",
    dataIndex: "initials",
    render: (text) => "xxxx-xxxx-xxxx-" + text
}, {
    title: "Generated By",
    dataIndex: ["user", "name"],
    sorter: Sorters.STRING("user.name")
}, {
    title: "Generated At",
    dataIndex: "createdAt",
    render: CellRenderers.TIME,
    sorter: Sorters.DATE("createdAt")
}, {
    title: "Key type",
    dataIndex: "apiKeyType",
    render: (apiKeyType) => _.find(API_TOKEN_TYPE_LIST, { value: apiKeyType })?.label ?? "User"
}, {
    title: "Last used",
    dataIndex: "lastUsedAt",
    render: value => {
        const time = CellRenderers.TIME_RELATIVE(value);
        if(time === "-"){
            return "Never";
        }
        return time;
    },
    sorter: Sorters.DATE("lastUsedAt")
}, {
    title: "Status",
    dataIndex: "active",
    sorter: Sorters.NUMBER("active"),
    width:150,
    render: (active, record) => {
        return (
            <>
                {active ? "Active" : "Inactive"}
                <Divider type="vertical" />
                <Typography.Link disabled data-id={record.id} data-status={active}>
                    Make {active ? "inactive" : "active"}
                </Typography.Link>
            </>
        )
    }
}, {
    title: "Action",
    dataIndex: "active",
    width:100,
    render: () => {
        return (
          <div style={{marginTop:'4px'}}><AuthorisedContainer permission={PermissionSetMap.DELETE_TOOL}><DeleteButton confirmText="Delete"/></AuthorisedContainer></div>
        )
    },
}]


  const getColumns = () => {
    switch(name){
      case "Email":
        return emailColumns;
      case "ArmorCode API":
        return armorCodeApi;
      default:
        return pushBasedColumns
    }
  }


  return (
    <TableContainer
      style={{ overflowY: 'hidden' }}
      columns={getColumns()}
      dataSource={data}
      showSizeChanger={true}
      locale={{ emptyText: "No configurations" }}
      extras={extras}
      defaultFilter={defaultFilter}
    />
  )
}